import { FNC_CHAR } from './scan.const';
import { BarcodeElements } from './barcode-elements';
import { ElementType } from './scan.enum';
import { ScanResult } from './scan.interface';

/**
 * some data items are followed by an FNC even in case of
 * fixed length, so the codeStringToReturn may have
 * leading FNCs.
 *
 * This function eliminates these leading FNCs.
 *
 * @param   stringToClean string which has to be cleaned
 * @returns the cleaned string
 */
export function cleanCodeString(stringToClean: string): string {
    let firstChar = stringToClean.slice(0, 1);
    while (firstChar === FNC_CHAR) {
        stringToClean = stringToClean.slice(1, stringToClean.length);
        firstChar = stringToClean.slice(0, 1);
    }
    return stringToClean;
}

export function parseVariableLength(ai: string, title: string, codeString: string): ScanResult {
    const element = new BarcodeElements(ai, title, ElementType.String);
    const offSet = ai.length;
    const posOfFNC = codeString.indexOf(FNC_CHAR);
    if (posOfFNC === -1) {
        // we've got the last element of the barcode
        element.data = codeString.slice(offSet, codeString.length);
        return { element, codeStringToReturn: '' };
    } else {
        element.data = codeString.slice(offSet, posOfFNC);
        return { element, codeStringToReturn: codeString.slice(posOfFNC + 1, codeString.length) };
    }
}

/**
 * Used for calculating numbers which are given as string
 * with a given number of fractional decimals.
 *
 * To avoid conversion errors binary <-> decimal I _don't_
 * just divide by 10 numberOfFractionals times.
 *
 * @param stringToParse the string to parse
 * @param numberOfFractional the number of fractional decimals
 * @returns the parsed float
 * @throws Error if parsing fails
 */
export function parseFloatingPoint(stringToParse: string, numberOfFractional: number): number {
    const offset = stringToParse.length - numberOfFractional;
    let auxFloat = 0.0;

    const auxString = stringToParse.slice(0, offset) + '.' + stringToParse.slice(offset, stringToParse.length);
    try {
        auxFloat = parseFloat(auxString);
    } catch (error) {
        throw new Error(`error in parseFloatingPoint '${error}'`);
    }
    return auxFloat;
}

/**
 * parses data elements of variable length, which additionally have
 *
 * - an indicator for the number of valid decimals
 * - an implicit unit of measurement
 *
 * These data elements contain e.g. a weight or length.
 *
 * @param ai_stem the application identifier stem
 * @param fourthNumber the fourth number of the application identifier
 * @param title the title to use for the ParsedElement
 * @param unit the unit of measurement
 * @param codeString the code string to parse
 * @returns the parsed element and the remaining code string
 */
export function parseVariableLengthMeasure(
    ai_stem: string,
    fourthNumber: string,
    title: string,
    unit: string,
    codeString: string,
): ScanResult {
    // the place of the decimal fraction is given by the fourth number, that's
    // the first after the identifier itself.
    const element = new BarcodeElements(ai_stem + fourthNumber, title, ElementType.Number);
    const offSet = ai_stem.length + 1;
    const posOfFNC = codeString.indexOf(FNC_CHAR);
    const numberOfDecimals = parseInt(fourthNumber, 10);
    let numberPart;
    let codeStringToReturn = '';

    if (posOfFNC === -1) {
        numberPart = codeString.slice(offSet, codeString.length);
    } else {
        numberPart = codeString.slice(offSet, posOfFNC);
        codeStringToReturn = codeString.slice(posOfFNC + 1, codeString.length);
    }
    // adjust decimals according to this.fourthNumber:

    element.data = parseFloatingPoint(numberPart, numberOfDecimals);
    element.unit = unit;
    return { element, codeStringToReturn };
}

/**
 * simple: the element has a fixed length AND is not followed by an FNC1.
 *
 * @param  ai     the AI to use
 * @param  title  its title, i.e. its short description
 * @param  length the fixed length
 * @param  codeString the code string to parse
 * @returns the parsed element and the remaining code string
 */
export function parseFixedLength(ai: string, title: string, length: number, codeString: string): ScanResult {
    const element = new BarcodeElements(ai, title, ElementType.String);
    const offSet = ai.length;
    element.data = codeString.slice(offSet, length + offSet);
    const codeStringToReturn = codeString.slice(length + offSet, codeString.length);

    return { element, codeStringToReturn };
}

/**
 * tries to parse an element of variable length
 * some fixed length AIs are terminated by FNC1, so this function
 * is used even for fixed length items
 *
 * @param ai_stem the application identifier stem
 * @param fourthNumber the fourth number of the application identifier
 * @param  title its title, i.e. its short description
 * @param unit the unit of measurement
 * @param codeString the code string to parse
 * @returns the parsed element and the remaining code string
 */
export function parseFixedLengthMeasure(
    ai_stem: string,
    fourthNumber: string,
    title: string,
    unit: string,
    codeString: string,
): ScanResult {
    //
    const element = new BarcodeElements(ai_stem + fourthNumber, title, ElementType.Number);
    const offSet = ai_stem.length + 1;
    const numberOfDecimals = parseInt(fourthNumber, 10);
    const numberPart = codeString.slice(offSet, offSet + 6);

    element.data = parseFloatingPoint(numberPart, numberOfDecimals);

    element.unit = unit;
    const codeStringToReturn = codeString.slice(offSet + 6, codeString.length);
    return { element, codeStringToReturn };
}

/**
 * parses data elements of variable length, which additionally have
 *
 * - an indicator for the number of valid decimals
 * - an explicit unit of measurement
 *
 * These data element contain amounts to pay or prices.
 *
 * @param ai_stem the application identifier stem
 * @param fourthNumber the fourth number of the application identifier
 * @param  title its title, i.e. its short description
 * @param codeString the code string to parse
 * @returns the parsed element and the remaining code string
 */
export function parseVariableLengthWithISONumbers(
    ai_stem: string,
    fourthNumber: string,
    title: string,
    codeString: string,
): ScanResult {
    // an element of variable length, representing a number, followed by some ISO-code.
    const element = new BarcodeElements(ai_stem + fourthNumber, title, ElementType.Number);
    const offSet = ai_stem.length + 1;
    const posOfFNC = codeString.indexOf(FNC_CHAR);
    const numberOfDecimals = parseInt(fourthNumber, 10);
    let isoPlusNumbers;
    let codeStringToReturn = '';
    if (posOfFNC === -1) {
        isoPlusNumbers = codeString.slice(offSet, codeString.length);
    } else {
        isoPlusNumbers = codeString.slice(offSet, posOfFNC);
        codeStringToReturn = codeString.slice(posOfFNC + 1, codeString.length);
    }
    // cut off ISO-Code
    const numberPart = isoPlusNumbers.slice(3, isoPlusNumbers.length);
    element.data = parseFloatingPoint(numberPart, numberOfDecimals);

    element.unit = isoPlusNumbers.slice(0, 3);
    return { element, codeStringToReturn };
}

/**
 * parses data elements of variable length, which additionally have
 *
 * - an explicit unit of measurement or reference
 *
 * These data element contain countries, authorities within countries.
 *
 * @param ai_stem the application identifier stem
 * @param  title its title, i.e. its short description
 * @param codeString the code string to parse
 * @returns the parsed element and the remaining code string
 */
export function parseVariableLengthWithISOChars(ai_stem: string, title: string, codeString: string): ScanResult {
    // an element of variable length, representing a sequence of chars, followed by
    // some ISO-code.
    const element = new BarcodeElements(ai_stem, title, ElementType.String);
    const offSet = ai_stem.length;
    const posOfFNC = codeString.indexOf(FNC_CHAR);
    let isoPlusNumbers;
    let codeStringToReturn = '';

    if (posOfFNC === -1) {
        isoPlusNumbers = codeString.slice(offSet, codeString.length);
    } else {
        isoPlusNumbers = codeString.slice(offSet, posOfFNC);
        codeStringToReturn = codeString.slice(posOfFNC + codeString.length);
    }
    // cut off ISO-Code
    element.data = isoPlusNumbers.slice(3, isoPlusNumbers.length);
    element.unit = isoPlusNumbers.slice(0, 3);
    return { element, codeStringToReturn };
}

/**
 * dates in GS1-elements have the format "YYMMDD".
 * This function generates a new BarcodeElements and tries to fill a
 * JS-date into the "data"-part.
 *
 * @param  ai    the AI to use for the ParsedElement
 * @param  title the title to use for the ParsedElement
 * @param codeString the code string to parse
 * @returns the parsed element and the remaining code string
 * @throws Error if parsing fails
 */
export function parseDate(ai: string, title: string, codeString: string): ScanResult {
    const element = new BarcodeElements(ai, title, ElementType.Date);
    const offSet = ai.length;
    const dateYYMMDD = codeString.slice(offSet, offSet + 6);
    let yearAsNumber = 0;
    let monthAsNumber = 0;
    let dayAsNumber = 0;
    try {
        yearAsNumber = parseInt(dateYYMMDD.slice(0, 2), 10);
    } catch (error) {
        throw new Error(`error in parse date '${error}'`);
    }

    try {
        monthAsNumber = parseInt(dateYYMMDD.slice(2, 4), 10) - 1;
    } catch (error) {
        throw new Error(`error in parse date '${error}'`);
    }

    try {
        dayAsNumber = parseInt(dateYYMMDD.slice(4, 6), 10);
    } catch (error) {
        throw new Error(`error in parse date '${error}'`);
    }
    // we are in the 21st century, but section 7.12 of the specification
    // states that years 51-99 should be considered to belong to the
    // 20th century:
    if (yearAsNumber > 50) {
        yearAsNumber = yearAsNumber + 1900;
    } else {
        yearAsNumber = yearAsNumber + 2000;
    }

    (<Date>element.data).setUTCFullYear(yearAsNumber, monthAsNumber, dayAsNumber);
    const codeStringToReturn = codeString.slice(offSet + 6, codeString.length);
    return { element, codeStringToReturn };
}
